import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { videoObjectScript } from "@/lib/videoJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { TensionLightingSection } from "@/components/TensionLightingSection";
import { SeriesBadge } from "@/components/SeriesBadge";
import hero from "@/assets/lighting/outdoor-lighting-hero.jpg";
import smartControl from "@/assets/lighting/smart-lighting-control.jpg";
import outdoorChristmasLightsVideo from "@/assets/lighting/outdoor-christmas-lights-year-round-san-antonio.mp4.asset.json";
import accentDroneHero from "@/assets/lighting/accent-lighting-drone-hero.mp4.asset.json";
import laborInstallVideo from "@/assets/cooling/labor-install-misting.mp4.asset.json";
import nightPresetsVideo from "@/assets/cooling/night-presets-misting.mp4.asset.json";
import bossoLogo from "@/assets/lighting/bosso-smart-lighting-logo-white.png.asset.json";

const FAQS = [
  {
    q: "Which series does outdoor lighting belong to?",
    a: "Outdoor lighting is part of our Hill Country Series™ — Outdoor Living & Premium Shade, powered by Bosso Smart Lighting. As an authorized Bosso Smart Lighting dealer, we install it alongside retractable awnings, motorized screens, patio curtains, sail shades and outdoor cooling because lighting is what finishes an outdoor environment.",
  },
  {
    q: "What is Bosso Smart Lighting?",
    a: "Bosso Smart Lighting is the smart, app-controlled outdoor lighting line we install — warm-white and full-color fixtures, dimming, zones and scheduling, all controlled from your phone. We integrate it into awnings, pergolas, sail shades, patios, pools and landscape areas.",
  },
  {
    q: "Can the lighting be automated with my shade?",
    a: "Yes. Smart controls are available through our Lone Star Series™ smart automation solutions — motorized awnings, drop screens and lighting can run on the same schedules, scenes and sensors so the patio sets itself at sunset.",
  },
  {
    q: "Can you add lighting to a structure you didn't build?",
    a: "Absolutely. We add lighting to existing pergolas, patio covers, awnings, gazebos and landscape areas, and we can re-canvas or repair the shade at the same time.",
  },
  {
    q: "Do you install outdoor lighting outside San Antonio?",
    a: "Yes — San Antonio, Austin, New Braunfels, Seguin, Boerne, the Hill Country and Central and South Texas.",
  },
  {
    q: "Is outdoor lighting weather-rated for Texas?",
    a: "The fixtures we install are outdoor-rated for Texas heat, humidity and storms, wired in conduit or protected runs with proper transformers and GFCI protection.",
  },
];

export const Route = createFileRoute("/outdoor-lighting")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Outdoor Lighting San Antonio & Austin | Bosso Smart Lighting",
      description:
        "Outdoor lighting for patios, pergolas, awnings and pools — authorized Bosso Smart Lighting dealer. Hill Country Series™ Outdoor Living, installed across San Antonio & Austin.",
      image: hero,
      socialTitle: "Hill Country Series™ Outdoor Lighting — Authorized Bosso Smart Lighting Dealer",
      socialDescription:
        "App-controlled outdoor lighting for patios, pergolas, awnings, pools and landscapes across San Antonio, Austin and the Texas Hill Country.",
      keywords:
        "outdoor lighting San Antonio, outdoor lighting Austin, Bosso Smart Lighting, smart outdoor lighting Texas, patio lighting San Antonio, pergola lighting, landscape lighting San Antonio, awning lighting, pool lighting Texas, outdoor living lighting, smart lighting automation, permanent outdoor lighting, outdoor Christmas lights San Antonio, year round Christmas lights, permanent holiday lighting San Antonio",
    }),
    scripts: [
      ...serviceHeadScripts({
        path: "/outdoor-lighting",
        serviceName: "Outdoor Lighting — Bosso Smart Lighting",
        serviceType: "Outdoor and landscape lighting design, supply and installation",
        breadcrumbName: "Outdoor Lighting",
        description:
          "Hill Country Series™ outdoor lighting powered by Bosso Smart Lighting — patio, pergola, awning, pool and landscape lighting with app control, installed across San Antonio, Austin and the Texas Hill Country.",
        faqs: FAQS,
      }),
      videoObjectScript({
        path: "/outdoor-lighting",
        anchor: "lighting-install-video",
        name: "Outdoor Living Installation — Texas Made Shade",
        description:
          "Watch our crew install outdoor living systems for a Texas patio: pump, stainless .006 nozzles, hidden tubing and oscillating fan placement, all coordinated with the lighting that finishes the space after dark.",
        thumbnails: [hero],
        duration: "PT1M44S",
        uploadDate: "2026-09-08",
        contentUrl: laborInstallVideo.url,
        keywords:
          "outdoor living installation, patio installation Texas, outdoor lighting install, pergola lighting, landscape lighting San Antonio",
      }),
      videoObjectScript({
        path: "/outdoor-lighting",
        anchor: "lighting-night-presets",
        name: "Outdoor Living Night Presets & Demo — Texas Made Shade",
        description:
          "Evening demonstration of outdoor preset patterns across a lit patio. See how air movement, fine mist and lighting work together to create a comfortable finished night-time environment.",
        thumbnails: [hero],
        duration: "PT1M13S",
        uploadDate: "2026-09-08",
        contentUrl: nightPresetsVideo.url,
        keywords:
          "outdoor living demo, night patio demo, outdoor lighting Texas, Bosso Smart Lighting preset, patio lighting",
      }),
    ],
  }),
  component: OutdoorLightingPage,
});

function OutdoorLightingPage() {
  return (
    <ServicePage
      currentPath="/outdoor-lighting"
      serviceName="Outdoor Lighting"
      heroImageClassName="opacity-85"
      heroOverlayClassName="bg-gradient-to-r from-navy-deep/60 via-navy-deep/25 to-transparent"
      breadcrumbs={[
        { label: "Outdoor Living", to: "/hill-country-series" },
        { label: "Outdoor Lighting" },
      ]}
      keywordOutline={[
        { id: "overview", label: "Outdoor Lighting in San Antonio & Austin" },
        { id: "why-us", label: "Why Texas Made Shade for Outdoor Lighting" },
        { id: "details", label: "Bosso Smart Lighting Fixtures & Zones" },
        { id: "applications", label: "Where We Install Outdoor Lighting" },
        { id: "faqs", label: "Outdoor Lighting FAQs" },
      ]}
      eyebrow="★ HILL COUNTRY SERIES™ · OUTDOOR LIVING & PREMIUM SHADE ★"
      title="Outdoor Lighting — Authorized Bosso Smart Lighting Dealer"
      intro="Shade makes the space usable in the day. Lighting makes it yours at night. Our Hill Country Series™ outdoor living division designs and installs Bosso Smart Lighting on patios, pergolas, awnings, sail shades, pools and landscapes across San Antonio, Austin, New Braunfels and the Texas Hill Country — one source for the whole outdoor environment."
      heroVideo={{ src: accentDroneHero.url, poster: hero }}
      preContent={(
        <>
          <section className="bg-cream py-16 md:py-20">
            <div className="mx-auto max-w-5xl px-4 lg:px-8">
              <div className="text-center">
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
                  ★ YEAR-ROUND HOLIDAY LIGHTING ★
                </div>
                <h2 className="mt-3 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                  Outdoor Christmas Lights — Installed for Year-Round Use in San Antonio
                </h2>
                <p className="mx-auto mt-4 max-w-3xl text-lg text-navy-deep/85">
                  Permanent, track-mounted LED holiday lighting blends into your roofline, pergola or patio structure until you want it to shine. Run warm white for everyday elegance, switch to colors for holidays and game days, and schedule everything from your phone.
                </p>
              </div>
              <figure className="mt-10 overflow-hidden border-2 border-navy-deep shadow-card">
                <video
                  src={outdoorChristmasLightsVideo.url}
                  controls
                  playsInline
                  preload="metadata"
                  poster={hero}
                  aria-label="Outdoor Christmas lights year round San Antonio — permanent holiday lighting installation by Texas Made Shade"
                  className="block aspect-video w-full bg-navy-deep"
                />
                <figcaption className="bg-navy-deep px-4 py-3 text-center font-display text-xs uppercase tracking-[0.2em] text-white">
                  Outdoor Christmas lights year round San Antonio — permanent holiday lighting by Texas Made Shade
                </figcaption>
              </figure>
              <div className="mt-8 grid gap-6 text-navy-deep/85 md:grid-cols-3">
                <div className="border-l-4 border-red bg-white p-5 shadow-sm">
                  <h3 className="font-display font-bold uppercase tracking-wide text-navy-deep">Permanent Track</h3>
                  <p className="mt-2 text-sm">Low-profile channels mounted under eaves, fascia or pergola beams — the lights stay put and protected year-round.</p>
                </div>
                <div className="border-l-4 border-red bg-white p-5 shadow-sm">
                  <h3 className="font-display font-bold uppercase tracking-wide text-navy-deep">App-Controlled Colors</h3>
                  <p className="mt-2 text-sm">Warm white, festive colors, dimming and schedules from your phone. One system handles holidays, parties and everyday accent lighting.</p>
                </div>
                <div className="border-l-4 border-red bg-white p-5 shadow-sm">
                  <h3 className="font-display font-bold uppercase tracking-wide text-navy-deep">Same Crew, Same Warranty</h3>
                  <p className="mt-2 text-sm">Installed by our Seguin-based outdoor living crews as part of the Hill Country Series™ — not a third-party light hanger.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-navy-deep py-14 text-cream md:py-16">
            <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 lg:grid-cols-[minmax(0,260px)_1fr] lg:px-8">
              <div className="flex flex-col items-center gap-4 text-center">
                <SeriesBadge slug="hill-country-series" size="lg" priority />
                <div>
                  <div className="font-display text-xs uppercase tracking-[0.3em] text-cream/70">
                    Primary Badge
                  </div>
                  <div className="mt-1 font-display text-lg font-bold">Hill Country Series™</div>
                  <div className="text-sm text-cream/80">Outdoor Living &amp; Premium Shade</div>
                </div>
              </div>
              <div>
                <img
                  src={bossoLogo.url}
                  alt="Bosso Smart Lighting logo"
                  width={320}
                  height={80}
                  loading="eager"
                  decoding="async"
                  className="mb-5 h-auto w-full max-w-[280px]"
                />
                <h2 className="font-display text-3xl font-bold leading-tight md:text-4xl">
                  Authorized Bosso Smart Lighting Dealer — part of the Hill Country Series™
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-cream/85">
                  Lighting isn't a bolt-on accessory. It's part of creating the finished outdoor
                  environment — shade, sail shades, screens and curtains, cooling and misting,
                  furniture areas, fire pits, and the light that ties it all together after sundown.
                  That's why outdoor lighting sits in the Hill Country Series™ — Outdoor Living &amp; Premium Shade, not buried under electrical.
                </p>
                <p className="mt-4 text-lg leading-relaxed text-cream/85">
                  <strong>Smart controls are available through our{" "}
                  <Link to="/lone-star-elite" className="underline decoration-2 underline-offset-4 hover:text-white">
                    Lone Star Series™ automation solutions
                  </Link>{" "}
                  </strong>— so your lighting, motorized awnings and drop screens can run on the same
                  scenes, schedules and sensors.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/hill-country-series"
                    className="border-2 border-cream px-5 py-3 font-display text-sm uppercase tracking-wider hover:bg-cream hover:text-navy-deep"
                  >
                    Explore Outdoor Living →
                  </Link>
                  <Link
                    to="/lone-star-elite"
                    className="border-2 border-cream/50 px-5 py-3 font-display text-sm uppercase tracking-wider text-cream/90 hover:border-cream hover:text-white"
                  >
                    Smart Lighting &amp; Automation ⭐
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
      features={[
        "Bosso Smart Lighting — app-controlled zones, dimming & scenes",
        "Integrated into awnings, pergolas, patio covers & sail shades",
        "Warm-white and full-color outdoor-rated fixtures",
        "Landscape, path, uplight, step & pool-area lighting",
        "Works with Lone Star Series™ motor & screen automation",
        "Installed and serviced by our own Seguin-based crews",
      ]}
      body={[
        {
          heading: "One source for shade and light",
          text: "Most homeowners hire a shade company, then an electrician, then a landscape lighting company — and nobody owns the result. We design the shade structure and the lighting together, so conduit, transformers, fixture locations and controls are planned before the first bracket is welded.",
          bullets: [
            "Lighting laid out with the shade design, not after it",
            "Wire runs concealed in awning frames, posts and pergola beams",
            "Fixtures spec'd for Texas heat, humidity and storm exposure",
            "One crew, one warranty conversation, one phone number",
          ],
          cta: { label: "Talk to us about lighting", href: "/estimate-quotes#general" },
        },
        {
          heading: "Bosso Smart Lighting — control from your phone",
          text: "Bosso brings the smart side of outdoor lighting: separate zones for the patio, perimeter, trees and pool, dimming per zone, saved scenes for dinner or game night, color options for holidays and game days, and schedules that follow sunset instead of a fixed clock.",
          bullets: [
            "Zone-by-zone dimming and on/off from a phone app",
            "Saved scenes: dinner, entertaining, holiday, security",
            "Sunset-based scheduling and vacation modes",
            "Warm-white for everyday, full color when you want it",
          ],
          image: {
            src: smartControl,
            alt: "Homeowner using a phone app to dim warm-white Bosso Smart Lighting installed in a Texas patio pergola at dusk",
            caption: "Bosso Smart Lighting — zones, dimming and scenes from your phone.",
          },
        },
        {
          heading: "Cross-linked with Lone Star Series™ smart automation",
          text: "Lighting is the easiest place to start with automation, and the natural bridge to the rest of it. Through our Lone Star Series™ smart automation solutions, lighting scenes can trigger alongside motorized retractable awnings, motorized drop screens, misting and heat — one tap, or no tap at all.",
          bullets: [
            "Lighting + motorized awning + drop screen on one scene",
            "Sun, wind and temperature sensor triggers",
            "Voice and smart-home integration options",
            "Retrofit onto systems we already installed",
          ],
          cta: { label: "See Lone Star Series™ automation", href: "/lone-star-elite" },
        },
        {
          heading: "Part of the complete outdoor environment",
          text: "Shade + Lighting + Cooling + Screens + Automation + Hospitality. One Source. Every Detail. Every Solution. Outdoor lighting is how we finish the spaces we build — from a backyard patio in Schertz to a glamping property in the Hill Country.",
          bullets: [
            "Shade structures, patio covers and pergola covers",
            "Sail shades and tension structures",
            "Screens, drop curtains and privacy panels",
            "Outdoor cooling, misting and patio heat",
          ],
        },
      ]}
      applicationsEyebrow="APPLICATIONS"
      applicationsHeading="Where We Install Outdoor Lighting"
      applications={[
        "Patios & outdoor kitchens",
        "Pergolas & louvered structures",
        "Retractable awnings & fabric awnings",
        "Sail shades & tension structures",
        "Pool, spa & cabana areas",
        "Landscape, tree uplighting & pathways",
        "Decks, steps & railings",
        "Outdoor bars & entertaining areas",
        "Fire pit & lounge zones",
        "Glamping tents & hospitality patios",
        "Restaurant & commercial patios",
        "Event & venue spaces",
      ]}
      letsTalkImageSrc={hero}
      letsTalkImageAlt="Texas backyard patio at dusk with a lit retractable awning, uplit oak trees, path lighting and a glowing pool — outdoor lighting by Texas Made Shade"
      faqs={FAQS}
    >
      <section className="bg-navy-deep py-16 text-white md:py-20">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="text-center">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
              ★ SEE IT IN ACTION ★
            </div>
            <h2 className="mt-3 font-display text-3xl font-bold md:text-4xl">
              Outdoor Living Installation & Night Demo
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-lg text-white/85">
              Real footage from Texas Made Shade installs: the outdoor living systems going in, then the same space running at dusk with lighting and presets finishing the environment.
            </p>
          </div>
          <div className="mt-10 grid gap-8 md:grid-cols-2">
            <figure id="lighting-install-video" className="overflow-hidden border-2 border-white/10 bg-black shadow-card">
              <video
                src={laborInstallVideo.url}
                controls
                playsInline
                preload="metadata"
                aria-label="Outdoor living installation by Texas Made Shade — pump, nozzles and hidden tubing on a Texas patio"
                className="block aspect-video w-full"
              />
              <figcaption className="bg-navy-deep px-4 py-3 text-center font-display text-xs uppercase tracking-[0.2em] text-white">
                Outdoor living installation — pump, nozzles and hidden tubing
              </figcaption>
            </figure>
            <figure id="lighting-night-presets" className="overflow-hidden border-2 border-white/10 bg-black shadow-card">
              <video
                src={nightPresetsVideo.url}
                controls
                playsInline
                preload="metadata"
                aria-label="Night demo of outdoor living presets on a lit patio"
                className="block aspect-video w-full"
              />
              <figcaption className="bg-navy-deep px-4 py-3 text-center font-display text-xs uppercase tracking-[0.2em] text-white">
                Night preset demo — fine mist and lighting across the patio
              </figcaption>
            </figure>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a
              href="/estimate-quotes#general"
              className="border-2 border-cream bg-red px-6 py-3 font-display text-sm uppercase tracking-wider text-white hover:bg-red-deep"
            >
              Request a Lighting Quote →
            </a>
            <Link
              to="/outdoor-cooling-misting"
              className="border-2 border-cream px-6 py-3 font-display text-sm uppercase tracking-wider hover:bg-cream hover:text-navy-deep"
            >
              Outdoor Misting Cooling →
            </Link>
          </div>
        </div>
      </section>
    <TensionLightingSection />
    </ServicePage>
  );
}
